import { useContext, useCallback, useRef, useState, useEffect } from 'react';
import { StoreContext } from './store';
import { useAuth } from './useAuth';
import { 
  saveUserInteractionData, 
  saveUserSessionData, 
  syncPendingInteractions, 
  syncPendingSessions,
  getPendingInteractionsCount,
  getPendingSessionsCount,
  clearPendingData,
  isDenoKVBackendAvailable
} from '../../api/userInteractions';
import { isTrackingEnabled } from '../../api/trackingConfig';

// Constants for batching and rate limiting
const BATCH_SAVE_INTERVAL = 60000; // Save batches every 1 minute (60,000 ms)
const MAX_BATCH_SIZE = 50; // Maximum number of interactions to save in a single batch
const RATE_LIMIT_COMPONENT_VIEW = 30000; // 30 seconds
const RATE_LIMIT_PAGE_VIEW = 60000; // 60 seconds
const RATE_LIMIT_DEFAULT = 2000; // 2 seconds

/**
 * Custom hook for tracking user interactions
 * This hook provides functions for tracking different types of interactions
 * and saving them to DenoKV
 * 
 * @returns {Object} - Functions for tracking interactions
 */
function useUserInteractionTracker() {
  // Get the store context
  const store = useContext(StoreContext);
  const { user } = useAuth();

  // Refs to hold data and manage batching
  const pendingInteractions = useRef([]);
  const batchSaveTimeout = useRef(null);
  const lastComponentViewMap = useRef({});
  const lastInteractionTime = useRef({});
  const serverAvailableRef = useRef(true);
  
  // User and session refs
  const userRef = useRef(user);
  const useridRef = useRef(store?.userid);
  const sessionidRef = useRef(store?.sessionid);
  const setinteractiondataRef = useRef(store?.setinteractiondata);

  // State for tracking and pending interactions
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Update refs when dependencies change
  useEffect(() => {
    userRef.current = user;
    useridRef.current = store?.userid;
    sessionidRef.current = store?.sessionid;
    setinteractiondataRef.current = store?.setinteractiondata;
  }, [user, store?.userid, store?.sessionid, store?.setinteractiondata]);

  // Check for pending interactions and update count
  useEffect(() => {
    const updatePendingCount = () => {
      const interactionCount = getPendingInteractionsCount();
      const sessionCount = getPendingSessionsCount();
      setPendingCount(interactionCount + sessionCount);
    };

    // Update on mount
    updatePendingCount();

    // Set up interval to check periodically
    const intervalId = setInterval(updatePendingCount, 60000); // Check every minute

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Handle online/offline status and sync pending data
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingData();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    const syncPendingData = async () => {
      if (navigator.onLine) {
        try {
          // Check if the server is available
          const available = await isDenoKVBackendAvailable();
          serverAvailableRef.current = available;
          
          if (!available) {
            console.log('DenoKV server is not available, skipping sync');
            return;
          }
          
          // Sync pending interactions
          const syncedInteractions = await syncPendingInteractions();
          
          // Sync pending sessions
          const syncedSessions = await syncPendingSessions();
          
          // Update pending count
          if (syncedInteractions > 0 || syncedSessions > 0) {
            console.log(`Synced ${syncedInteractions} interactions and ${syncedSessions} sessions`);
            setPendingCount(getPendingInteractionsCount() + getPendingSessionsCount());
          }
        } catch (error) {
          console.error('Error syncing pending data:', error);
        }
      }
    };

    // Set up event listeners for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Sync on mount and set up interval for periodic sync
    syncPendingData();
    const syncIntervalId = setInterval(syncPendingData, 300000); // Sync every 5 minutes
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(syncIntervalId);
      
      // Clear any pending timeout
      if (batchSaveTimeout.current) {
        clearTimeout(batchSaveTimeout.current);
        batchSaveTimeout.current = null;
      }
    };
  }, []);

  // Save interactions to DenoKV
  const saveInteractionsToDenoKV = useCallback(async (interactions) => {
    if (!interactions || interactions.length === 0) {
      return;
    }

    try {
      // Get the current user ID and session ID
      const currentUserId = useridRef.current || userRef.current?.id;
      const currentSessionId = sessionidRef.current;

      // Save the interactions to DenoKV
      const result = await saveUserInteractionData(
        { interactions },
        currentUserId,
        currentSessionId
      );

      // Update server availability based on result
      if (result && result.offline === true) {
        serverAvailableRef.current = false;
      } else {
        serverAvailableRef.current = true;
      }

      // Update pending count
      setPendingCount(getPendingInteractionsCount() + getPendingSessionsCount());

      if (!result || result.success === false) {
        console.log(`Stored ${interactions.length} interactions for later sync`);
      } else {
        console.log(`Saved ${interactions.length} interactions to DenoKV`);
      }
    } catch (error) {
      console.error('Error saving interactions to DenoKV:', error);
      // Note: The saveUserInteractionData function already handles offline storage
    }
  }, []);

  // Save session data
  const saveSessionData = useCallback(async (sessionData) => {
    try {
      // Get the current user ID and session ID
      const currentUserId = useridRef.current || userRef.current?.id;
      const currentSessionId = sessionidRef.current;

      if (!currentUserId || !currentSessionId) {
        console.log('Session data would be saved to DenoKV:', sessionData);
        return;
      }

      // Save the session data to DenoKV
      const result = await saveUserSessionData(
        sessionData,
        currentUserId,
        currentSessionId
      );

      // Update server availability based on result
      if (result && result.offline === true) {
        serverAvailableRef.current = false;
      } else {
        serverAvailableRef.current = true;
      }

      // Update pending count
      setPendingCount(getPendingInteractionsCount() + getPendingSessionsCount());

      if (!result || result.success === false) {
        console.log('Session data stored for later sync');
      } else {
        console.log('Session data saved to DenoKV');
      }
    } catch (error) {
      console.error('Error saving session data to DenoKV:', error);
    }
  }, []);

  // Clear session data
  const clearSessionData = useCallback(() => {
    // Clear pending interactions
    pendingInteractions.current = [];
    
    // Clear any pending timeout
    if (batchSaveTimeout.current) {
      clearTimeout(batchSaveTimeout.current);
      batchSaveTimeout.current = null;
    }
    
    // Clear the interaction data in the store
    if (setinteractiondataRef.current) {
      setinteractiondataRef.current([]);
    }
    
    console.log('Session data cleared');
  }, []);

  // Clear all pending data
  const clearAllPendingData = useCallback(() => {
    clearPendingData();
    setPendingCount(0);
    console.log('All pending interaction and session data cleared');
  }, []);

  // Should the interaction be rate limited?
  const shouldRateLimit = useCallback((type, componentName) => {
    const now = Date.now();
    
    // Rate limit component_view interactions to once per component per 30 seconds
    if (type === 'component_view' && componentName) {
      const key = `${type}_${componentName}`;
      const lastTime = lastComponentViewMap.current[key] || 0;
      
      if (now - lastTime < RATE_LIMIT_COMPONENT_VIEW) {
        return true;
      }
      
      lastComponentViewMap.current[key] = now;
      return false;
    }
    
    // Rate limit page_view interactions to once per page per 60 seconds
    if (type === 'page_view') {
      const pageName = componentName || 'unknown';
      const key = `${type}_${pageName}`;
      const lastTime = lastInteractionTime.current[key] || 0;
      
      if (now - lastTime < RATE_LIMIT_PAGE_VIEW) {
        return true;
      }
      
      lastInteractionTime.current[key] = now;
      return false;
    }
    
    // Rate limit other interaction types to once per 2 seconds
    const key = type;
    const lastTime = lastInteractionTime.current[key] || 0;
    
    if (now - lastTime < RATE_LIMIT_DEFAULT) {
      return true;
    }
    
    lastInteractionTime.current[key] = now;
    return false;
  }, []);

  // Process the batch of pending interactions
  const processBatch = useCallback(async () => {
    // Don't process if there are no pending interactions
    if (pendingInteractions.current.length === 0) {
      return;
    }

    // Take a batch of interactions to process
    const batchSize = Math.min(pendingInteractions.current.length, MAX_BATCH_SIZE);
    const interactionsToSave = pendingInteractions.current.slice(0, batchSize);
    pendingInteractions.current = pendingInteractions.current.slice(batchSize);

    // Save the batch
    await saveInteractionsToDenoKV(interactionsToSave);

    // If there are still pending interactions, schedule another batch
    if (pendingInteractions.current.length > 0) {
      batchSaveTimeout.current = setTimeout(() => {
        processBatch();
        batchSaveTimeout.current = null;
      }, BATCH_SAVE_INTERVAL);
    }
  }, [saveInteractionsToDenoKV]);

  // Track a user interaction
  const trackInteraction = useCallback(async (type, data = {}, options = {}) => {
    // Skip tracking if disabled or explicitly skipped
    if (!trackingEnabled || options.skipTracking) {
      return;
    }

    // Apply rate limiting unless explicitly bypassed
    if (!options.bypassRateLimit && shouldRateLimit(type, data.componentName)) {
      return;
    }

    // Check if tracking is enabled for this component and event type
    if (data.componentName && type) {
      try {
        const enabled = await isTrackingEnabled(data.componentName, type);
        if (!enabled) {
          return;
        }
      } catch (error) {
        // Continue with tracking if there's an error checking
        console.warn(`Error checking if tracking is enabled for ${data.componentName}:${type}`, error);
      }
    }

    // Create the interaction object
    const interaction = {
      id: crypto.randomUUID(),
      type,
      timestamp: data.timestamp || Date.now(),
      userId: useridRef.current || userRef.current?.id,
      sessionId: sessionidRef.current,
      ...data
    };

    // Add to pending interactions
    pendingInteractions.current.push(interaction);

    // Update the interaction data in the store
    if (setinteractiondataRef.current) {
      setinteractiondataRef.current(prev => {
        const newData = Array.isArray(prev) ? [...prev, interaction] : [interaction];
        // Limit the size of the interaction data in the store
        if (newData.length > 1000) {
          return newData.slice(-1000); // Keep only the most recent 1000 interactions
        }
        return newData;
      });
    }

    // Save immediately if requested
    if (options.immediate) {
      // Clear any existing timeout
      if (batchSaveTimeout.current) {
        clearTimeout(batchSaveTimeout.current);
        batchSaveTimeout.current = null;
      }

      // Process the batch immediately
      await processBatch();
    } else {
      // Set up batch saving if not already scheduled
      if (!batchSaveTimeout.current) {
        batchSaveTimeout.current = setTimeout(() => {
          processBatch();
          batchSaveTimeout.current = null;
        }, BATCH_SAVE_INTERVAL);
      }
    }
  }, [trackingEnabled, processBatch, shouldRateLimit]);

  return {
    trackInteraction,
    saveSessionData,
    clearSessionData,
    clearAllPendingData,
    trackingEnabled,
    setTrackingEnabled,
    pendingCount,
    isOnline,
    isServerAvailable: serverAvailableRef.current
  };
}

export default useUserInteractionTracker;
